/**
 * Modern centralized date formatting utilities
 * Zero-tolerance for invalid dates, consistent error handling
 */

/**
 * Shared guard to check if a value is a Date instance.
 * Use sparingly: date normalization should happen at the API/contract boundary.
 * Reaching for this helper in application code is usually a signal that the boundary
 * still emits mixed string/Date payloads that should be fixed instead.
 */
export function isDate(value: unknown): value is Date {
  return typeof value === 'object' && value !== null && Object.prototype.toString.call(value) === '[object Date]';
}

/**
 * Internal helper to validate a Date object
 * Exported for use in date-formatters.ts only - do not use elsewhere
 */
export function validateDate(input: Date): Date | null {
  return isDate(input) && !Number.isNaN(input.getTime()) ? input : null;
}

/**
 * Format a Date object to a localized date and time string
 * @param input Date instance (should be already parsed at boundary)
 * @returns Formatted date string or 'Never' for invalid dates
 */
export function formatDateTime(input: Date | null | undefined): string {
  if (!input) {
    return 'Never';
  }
  const date = validateDate(input);
  return date ? date.toLocaleString() : 'Never';
}

/**
 * Format a Date object to ISO string
 * @param input Date instance (should be already parsed at boundary)
 * @returns ISO string or current timestamp for invalid dates
 */
export function formatToISO(input: Date | null | undefined): string {
  if (!input) {
    return new Date().toISOString();
  }
  const date = validateDate(input);
  return date ? date.toISOString() : new Date().toISOString();
}

/**
 * Format a Date object to a localized date string (no time)
 * @param input Date instance (should be already parsed at boundary)
 * @returns Formatted date string or 'Never' for invalid dates
 */
export function formatDate(input: Date | null | undefined): string {
  if (!input) {
    return 'Never';
  }
  const date = validateDate(input);
  return date ? date.toLocaleDateString() : 'Never';
}

/**
 * Format relative time string with smart precision
 * @param input Date instance (should be already parsed at boundary)
 * @returns Formatted relative time or 'Never' for invalid dates
 */
export function formatTimeAgo(input: Date | null | undefined): string {
  if (!input) {
    return 'Never';
  }
  const date = validateDate(input);
  if (!date) {
    return 'Never';
  }

  const now = Date.now();
  const diff = now - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years > 0) {
    return `${years}y ago`;
  }
  if (months > 0) {
    return `${months}mo ago`;
  }
  if (weeks > 0) {
    return `${weeks}w ago`;
  }
  if (days > 0) {
    return `${days}d ago`;
  }
  if (hours > 0) {
    return `${hours}h ago`;
  }
  if (minutes > 0) {
    return `${minutes}m ago`;
  }
  return 'Just now';
}

/**
 * Serialize a Date object to an ISO string or null.
 * Date must already be parsed at the boundary.
 * This is the canonical date serialization utility.
 */
export function toIsoStringOrNull(value: Date | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = validateDate(value);
  return date ? date.toISOString() : null;
}

/**
 * Serialize a Date object to an ISO string or undefined.
 * Useful for serialized payloads that omit missing fields entirely.
 */
export function toIsoStringOrUndefined(value: Date | null | undefined): string | undefined {
  const iso = toIsoStringOrNull(value);
  return iso === null ? undefined : iso;
}

/**
 * Format a Date object to digest timestamp format (e.g., "Jan 15, 2025 10:30:45 AM UTC")
 */
export function formatDigestTimestamp(value: Date | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = validateDate(value);
  if (!date) {
    return null;
  }
  const formatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
  return formatter.format(date);
}

export { parseTimestampToDate, parseTimestampToNumber } from '@sweetistics/contracts/utils/time';
export { formatLocaleNumber as formatNumber } from '../formatters/numbers';
